class GoogleMap extends HTMLElement {
  static get observedAttributes() {
    return ['country-code'];
  }

  static selectors = {
    map: '.js-google-maps-map',
    countrySelect: '.js-e-dealersearch-country',
    markerList: '.js-e-dealersearch-list',
    markerListItem: '.js-e-dealersearch-list-item',
    useLocationBtn: '.js-google-maps-use-location',
    searchBtn: '.js-google-maps-search-btn',
    searchInput: '.js-google-maps-search-input',
    markerListItemTemplate: '#marker-list-item-template',
    markerInfoTemplate: '#marker-info-window-template',
  };

  static settings = {
    closestMarkers: 3,
    initialZoom: 8,
  };

  static events = {
    click: 'click',
    change: 'change',
    gMapClick: 'gmp-click',
    submit: 'submit',
  };

  constructor() {
    super();
    this.map = null;
    this.gMarkers = [];
    this.markerData = [];
    this.countryCodes = [];
    this.markerCluster = null;
    this.infoWindow = null;
    this.countryCode = this.getAttribute( 'country-code' ) || 'DK';
    this.mapsId = this.getAttribute( 'maps-id' ) || 'DEMO_MAP_ID';
    this.apiKey = this.getAttribute( 'api-key' ) || '';
    this.unitSystem = this.getAttribute( 'unit-system' ) || '';
    this.groupId = this.getAttribute( 'group-id' ) || '';
    this.sortOrigin = null;
    this.countrySelects = this.querySelectorAll( GoogleMap.selectors.countrySelect );
    this.markerListItemTemplate = this.querySelector( GoogleMap.selectors.markerListItemTemplate );
    this.markerInfoWindowTemplate = this.querySelector( GoogleMap.selectors.markerInfoTemplate );

  }

  connectedCallback() {
    if ( !window.google || !window.google.maps ) {
      this.loadGoogleMapsScript().then( () => this.initMap() );
    } else {
      this.initMap();
    }
    this.addEventListener( GoogleMap.events.click, this, true );
    this.addEventListener( GoogleMap.events.change, this, true );
    this.addEventListener( GoogleMap.events.submit, this, true );
    this.countrySelects.forEach( s => s.value = this.countryCode );
  }

  attributeChangedCallback( name, oldVal, newVal ) {
    if ( name === 'country-code' && oldVal !== newVal && this.map ) {
      this.countryCode = newVal;
      this.countrySelects.forEach( s => s.value = this.countryCode );
      this.loadMarkers( newVal );
    }
  }

  handleEvent( event ) {
    switch ( event.type ) {
      case GoogleMap.events.click:
        if ( event.target.matches( GoogleMap.selectors.useLocationBtn ) || event.target.closest( GoogleMap.selectors.useLocationBtn ) ) {
          this.useLocation();
        }
        if ( event.target.matches( GoogleMap.selectors.markerListItem ) || event.target.closest( GoogleMap.selectors.markerListItem ) ) {
          let target = event.target;
          if ( target.tagName !== 'A' ) {
            if ( event.target.closest( GoogleMap.selectors.markerListItem ) ) {
              target = event.target.closest( GoogleMap.selectors.markerListItem );
            }
            const gMarker = this.gMarkers.find( marker => marker.metadata.id == target.dataset.markerId );
            this.showInfoWindow( gMarker );

          }

        }
        if ( event.target.matches( GoogleMap.selectors.searchBtn ) || event.target.closest( GoogleMap.selectors.searchBtn ) ) {
          const form = event.target.closest( 'form' );
          const input = form.querySelector( GoogleMap.selectors.searchInput );
          if ( input ) {
            this.search( input.value );
          }

        }
        break;

      case GoogleMap.events.change:
        if ( event.target.matches( GoogleMap.selectors.countrySelect ) ) {
          this.countryCode = event.target.value;
          this.setAttribute( 'country-code', this.countryCode );
        }
        break;
      case GoogleMap.events.submit:
        event.preventDefault();
        const input = event.target.querySelector( GoogleMap.selectors.searchInput );
        this.search( input.value );
        break;

    }

  }

  async loadGoogleMapsScript() {
    if ( !document.getElementById( 'gmap-js' ) ) {
      await new Promise( ( resolve ) => {
        const script = document.createElement( 'script' );
        script.id = 'gmap-js';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${ this.apiKey }&v=weekly&libraries=marker,geometry`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        document.head.appendChild( script );
      } );
    }

    if ( !window.markerClusterer ) {
      await new Promise( ( resolve ) => {
        const script = document.createElement( 'script' );
        script.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
        script.onload = resolve;
        document.head.appendChild( script );
      } );
    }
  }

  async initMap() {
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary( 'marker' );

    const map = this.querySelector( GoogleMap.selectors.map );

    this.map = new google.maps.Map( map, {
      center: { lat: 56.2639, lng: 9.5018 },
      zoom: GoogleMap.settings.initialZoom,
      mapId: this.mapsId,
      mapTypeControl: false,
      scrollwheel: false,
    } );

    this.map.addListener( 'idle', () => {
      this.renderList();
    } );

    this.infoWindow = new google.maps.InfoWindow();
    await this.loadMarkers( this.countryCode );
  }

  async loadMarkers( countryCode ) {
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary( 'marker' );

    if ( this.markerCluster ) this.markerCluster.clearMarkers();
    this.gMarkers.forEach( marker => marker.map = null );
    this.gMarkers = [];

    const bounds = new google.maps.LatLngBounds();
    const response = await fetch( `${ e4.settings.dealersearch.url }?countryCode=${ this.countryCode }&groupId=${ this.groupId }` );
    const data = await response.json();

    this.markerData = [...data.users];
    this.countryCodes = [...data.countryCodes];

    for ( const location of this.markerData ) {

      const position = new google.maps.LatLng( location.lat, location.lng );

      const marker = new AdvancedMarkerElement( {
        map: this.map,
        position,
        content: this.getMarkerIcon(),
        title: location.name,
      } );

      marker.metadata = { ...location };

      marker.addListener( GoogleMap.events.gMapClick, () => {
        this.showInfoWindow( marker );
      } );

      this.gMarkers.push( marker );
      bounds.extend( position );
    }

    if ( !bounds.isEmpty() ) {
      this.map.fitBounds( bounds, {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50,
      } );
    }

    this.markerCluster = new markerClusterer.MarkerClusterer( {
      map: this.map,
      markers: this.gMarkers,
    } );
    this.trimCountryCodes();
    this.renderList();
  }

  showInfoWindow( marker ) {
    this.map.panTo( marker.position );
    this.map.setZoom( GoogleMap.settings.initialZoom );
    const data = marker.metadata;
    let distance = '';
    
    if(this.sortOrigin){
      distance = `${(google.maps.geometry.spherical.computeDistanceBetween( this.sortOrigin, marker.position ) / 1000).toFixed(1)} KM`;
    }

    const clone = this.markerInfoWindowTemplate.content.cloneNode( true );
    const root = this.updateTemplateData(clone, data);

    this.infoWindow.setHeaderContent( distance ?? '' );
    this.infoWindow.setContent(root.innerHTML);
    this.infoWindow.open( this.map, marker );
  }

  fitBoundsToNearest( centerPosition = null ) {
    const distances = this.gMarkers.map( marker => {
      if ( centerPosition === null ) {
        centerPosition = this.map.getCenter();

      }
      const dist = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng( centerPosition ),
          marker.position,
      );
      return { marker, dist };
    } );

    const closest = distances.sort( ( a, b ) => a.dist - b.dist ).slice( 0, GoogleMap.settings.closestMarkers );
    const bounds = new google.maps.LatLngBounds();
    closest.forEach( ( { marker } ) => bounds.extend( marker.position ) );
    bounds.extend( centerPosition );

    this.map.fitBounds( bounds );
  }

  fitBoundsToCountry( countryCode ) {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode( { componentRestrictions: { country: countryCode } }, ( results, status ) => {
      if ( status === 'OK' && results[0] ) {
        const result = results[0];

        if ( result.geometry.bounds ) {
          this.map.fitBounds( result.geometry.bounds );
        } else {
          this.map.setCenter( result.geometry.location );
          this.map.setZoom( GoogleMap.settings.initialZoom );
        }
      } else {
        console.error( `Geocode was not successful for the following reason: ${ status }` );
      }
    } );
  }

  renderList() {
    const list = this.querySelector( GoogleMap.selectors.markerList );
    list.innerHTML = '';

    const bounds = this.map.getBounds();

    if ( this.sortOrigin && google.maps.geometry ) {
      this.markerData.sort( ( a, b ) => {
        const gMarkerA = this.gMarkers.find( m => m.metadata.id === a.id );
        const gMarkerB = this.gMarkers.find( m => m.metadata.id === b.id );
        const da = google.maps.geometry.spherical.computeDistanceBetween( this.sortOrigin, gMarkerA.position );
        const db = google.maps.geometry.spherical.computeDistanceBetween( this.sortOrigin, gMarkerB.position );
        return da - db;
      } );
    }

    this.markerData.forEach( marker => {
      const gMarker = this.gMarkers.find( m => m.metadata.id === marker.id );
      if ( !bounds || !bounds.contains( gMarker.position ) ) return;
      
      if(!gMarker.metadata.image){
        //gMarker.metadata.image = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
      }
      
      const clone = this.markerListItemTemplate.content.cloneNode( true );
      const root = this.updateTemplateData(clone, gMarker.metadata);
      
      const div = document.createElement( 'div' );
      div.dataset.markerId = gMarker.metadata.id;
      div.className = 'mb-1 js-e-dealersearch-list-item e-dealersearch-list-item';
      div.appendChild(root);
      list.appendChild(div);
    } );
  }

  search( query ) {
    if ( !query ) return;

    this.querySelectorAll( GoogleMap.selectors.searchInput ).forEach( i => i.value = query);

    try {
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode( { address: query }, ( results, status ) => {
        if ( status === 'OK' && results.length > 0 ) {
          const location = results[0].geometry.location;
          this.sortOrigin = location;
          this.map.setCenter( location );
          this.fitBoundsToNearest( location );
        } else {
          alert( 'No location found for that search.' );
        }
      } );
    } catch ( err ) {
      console.error( 'Geocode error:', err );
    }
  }

  useLocation() {
    if ( !navigator.geolocation ) {
      alert( 'Geolocation is not supported by your browser.' );
      return;
    }

    navigator.geolocation.getCurrentPosition(
        ( position ) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          if ( this.userMarker ) {
            this.userMarker.setMap( null );
          }

          this.userMarker = new google.maps.Marker( {
            position: userPos,
            map: this.map,
            title: 'You are here',
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: '#db052b',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
          } );

          this.fitBoundsToNearest( userPos );
        },
        () => alert( 'Unable to retrieve your location.' ),
    );
  }

  getMarkerIcon() {
    const pinPath = e4.settings.dealersearch.marker.iconUrl + e4.settings.dealersearch.marker.iconFileName;
    const iconImg = document.createElement( 'img' );
    iconImg.src = pinPath;
    return iconImg;
  }

  trimCountryCodes() {
    const dealerCountryCodes = this.countryCodes;
    this.countrySelects.forEach( ( countrySelect ) => {
      for ( let i = countrySelect.options.length - 1; i >= 0; i-- ) {
        const option = countrySelect.options[i];
        if ( !dealerCountryCodes.includes( option.value ) ) {
          countrySelect.removeChild( option );
        }
      }
    } );
  }
  
  updateTemplateData(clone, markerData){
    const root = clone.firstElementChild;
    root.innerHTML = root.innerHTML.replace(/{(.*?)}/g, (_, key) => markerData[key] || '');

    root.querySelectorAll('[data-if]').forEach(el => {
      const key = el.getAttribute('data-if');
      if (!markerData[key]) {
        el.remove();
      }
    });
    return root;
  }
}

customElements.define( 'google-map', GoogleMap );